import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UIMemoTag, UITagItem } from '../model/UiShapes';
import { handleError } from '../hooks/managers/errorHandler';
import { fetchMemoTag, fetchAllMemoTags } from '../hooks/api/memoTagAPI';

// 标签状态接口
export interface TagState {
  // State
  memoTags: UIMemoTag[];
  currentMemoTag: UIMemoTag | null;
  status: 'idle' | 'loading' | 'failed';
  isSyncing: boolean;
  error: string | null;
  searchTags: string[];
  selectedTags: string[];
  isLoading: boolean;

  // Actions
  setMemoTags: (tags: UIMemoTag[]) => void;
  addMemoTag: (tag: UIMemoTag) => void;
  updateMemoTag: (memoId: string, tags: UITagItem[]) => void;
  setCurrentMemoTag: (tag: UIMemoTag | null) => void;
  setCurrentMemoTagByMemoId: (memoId: string) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setIsSyncing: (syncing: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clear: () => void;
  setLoading: (loading: boolean) => void;

  // Tag filter actions
  setSearchTags: (tags: string[]) => void;
  setSelectedTag: (tags: string[]) => void;
  addSelectedTag: (tag: string) => void;
  removeSelectedTag: (tag: string) => void;

  // Async Actions
  fetchMemoTagAsync: (memoId: string) => Promise<void>;
  fetchAllMemoTagsAsync: () => Promise<void>;

  // 强制刷新方法
  forceRefreshMemoTagByMemoId: (memoTag: UIMemoTag | null) => void;
}

// 创建tagStore
export const useTagStore = create<TagState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      memoTags: [],
      currentMemoTag: null,
      status: 'idle',
      isSyncing: false,
      error: null,
      searchTags: [],
      selectedTags: [],
      isLoading: false,

      // 异步Actions实现
      fetchMemoTagAsync: async (memoId: string) => {
        set({ status: 'loading', error: null });
        try {
          // 调用API获取memo标签
          const memoTag = await fetchMemoTag(memoId);

          if (memoTag) {
            // 更新store状态
            set((state: TagState) => ({
              memoTags: [...state.memoTags.filter((tag: UIMemoTag) => tag.memoId !== memoId), memoTag],
              currentMemoTag: memoTag, // 自动设置当前memo的标签
              status: 'idle'
            }));
          } else {
            // 如果未找到标签，创建空的标签对象
            const emptyTag: UIMemoTag = { memoId, tags: [] };
            set((state: TagState) => ({
              memoTags: [...state.memoTags.filter((tag: UIMemoTag) => tag.memoId !== memoId), emptyTag],
              currentMemoTag: emptyTag, // 自动设置当前memo的标签
              status: 'idle'
            }));
          }
        } catch (error) {
          handleError(error, { context: 'fetchMemoTagAsync执行失败' });
          set({ status: 'failed', error: '获取标签失败' });
        }
      },

      fetchAllMemoTagsAsync: async () => {
        set({ status: 'loading', error: null });
        try {
          // 调用API获取所有标签
          const allMemoTags = await fetchAllMemoTags();

          if (allMemoTags && allMemoTags.length > 0) {
            // 更新store状态
            set({ memoTags: allMemoTags, status: 'idle' });
          } else {
            // 如果未找到标签，清空标签列表
            set({ memoTags: [], status: 'idle' });
          }
        } catch (error) {
          handleError(error, { context: 'fetchAllMemoTagsAsync执行失败' });
          set({ status: 'failed', error: '获取所有标签失败' });
        }
      },

      // Actions
      setMemoTags: (tags: UIMemoTag[]) => {
        // 确保tags始终是数组类型
        if (!Array.isArray(tags)) {
          handleError(new Error('setMemoTags: 接收到非数组类型'), { context: 'tagStore', showToast: false });
          set({ memoTags: [] });
        } else {
          set({ memoTags: tags });
        }
      },

      addMemoTag: (tag: UIMemoTag) => {
        set((state) => ({
          memoTags: [...state.memoTags.filter((t) => t.memoId !== tag.memoId), tag]
        }));
      },

      updateMemoTag: (memoId: string, tags: UITagItem[]) => {
        set((state) => {
          const updatedTag: UIMemoTag = {
            memoId: memoId,
            tags: tags
          };

          return {
            memoTags: [...state.memoTags.filter((tag: UIMemoTag) => tag.memoId !== memoId), updatedTag],
            currentMemoTag: updatedTag
          };
        });
      },

      setCurrentMemoTag: (tag: UIMemoTag | null) => {
        set({ currentMemoTag: tag });
      },

      setCurrentMemoTagByMemoId: (memoId: string) => {
        // 缓存优先策略：首先检查store缓存中是否已有该memo的标签
        const existingTag = get().memoTags.find((tag: UIMemoTag) => tag.memoId === memoId);

        if (existingTag) {
          set({ currentMemoTag: existingTag });
        } else {
          // 找不到标签时返回null，而不是创建空对象
          set({ currentMemoTag: null });
        }
      },

      setStatus: (status: 'idle' | 'loading' | 'failed') => set({ status }),

      setIsSyncing: (syncing: boolean) => set({ isSyncing: syncing }),

      setError: (error: string | null) => set({ error }),

      clearError: () => set({ error: null }),

      clear: () => {
        set({
          memoTags: [],
          currentMemoTag: null,
          status: 'idle',
          isSyncing: false,
          error: null,
          searchTags: [],
          selectedTags: [],
          isLoading: false
        });
      },

      // Tag filter actions
      setSearchTags: (tags: string[]) => set({ searchTags: tags }),

      setSelectedTag: (tags: string[]) => set({ selectedTags: tags }),

      addSelectedTag: (tag: string) =>
        set((state) => ({
          selectedTags: state.selectedTags.includes(tag) ? state.selectedTags : [...state.selectedTags, tag]
        })),

      removeSelectedTag: (tag: string) =>
        set((state) => ({
          selectedTags: state.selectedTags.filter((t) => t !== tag)
        })),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      // 强制刷新特定memo的标签数据（同步版本，由Manager层调用）
      forceRefreshMemoTagByMemoId: (memoTag: UIMemoTag | null) => {
        if (memoTag) {
          // 更新store状态
          get().addMemoTag(memoTag);
          set({ status: 'idle', error: null });
        } else {
          // 清除对应的缓存 - 当memoTag为null时，不需要清除特定memo的缓存
          set({ status: 'idle', error: null });
        }
      }
    }),
    {
      name: 'tag-store'
    }
  )
);
